// ═══════════════════════════════════════════════════════════════════
// willieActing.js — WHAT WILLIE CAN DO WITH HIS HANDS   (caP'n, 2026-09-25)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "one more proper rigging pass … make sure fingers limbs etc are good so
// we can do all kinds of expressions … have him pick up the broom sometimes
// eventually" → "we don't need him doing all that yet though, just the ability".
//
// THE ABILITIES. Nothing here decides WHEN he does anything — every one is a
// switch on `ctl` that a scene (the Plaza, a cutscene, the dialogue stage) flips:
//
//   ctl.hands = { R: "fist", L: "book" }   a hand shape per hand (HAND_SHAPES),
//                                          or raw curls { index, middle, ring, pinky, thumb, spread }
//   ctl.act   = "wave" | "present" | "beckon" | "shrug" | "thumbs_up" | "facepalm" | "wipe_tear" | "sweep"
//   ctl.book  = false                      he puts the book away (default true: he reads)
//   ctl.hold  = "broom"                    the broom in his right hand (act "sweep" sweeps with it)
//
// Everything EASES in and out (no pops), and every ability stacks on top of
// williePose and his faces (ctl.expr / ctl.talk), so "sad + shrug" or
// "smile + wave + talk" just work. Review room: ?act=wave&hands=R:open&book=0&hold=broom
//
// ⚠ Needs the RIGGED willie.glb (2026-09-25: 39 joints — index1R … thumb2L).
// On a glb without finger bones (the Forge's frozen willie_forge.glb) the hand
// shapes do nothing and the arm acts still play.
// PERF LAW: nothing here allocates per frame except the broom's two scratch vectors (module-level).
// ═══════════════════════════════════════════════════════════════════
import * as THREE from "three";

export const FINGERS = ["index", "middle", "ring", "pinky"];

// curls in radians at the knuckle; the second joint takes CURL2 × that.
// thumb: how far it folds across the palm. spread: fingers fan apart (+) / squeeze (−).
export const HAND_SHAPES = {
  relaxed:   { index: 0.25, middle: 0.3, ring: 0.35, pinky: 0.4, thumb: 0.15, spread: 0.05 },
  open:      { index: -0.12, middle: -0.1, ring: -0.1, pinky: -0.08, thumb: -0.25, spread: 0.18 },
  fist:      { index: 1.55, middle: 1.6, ring: 1.6, pinky: 1.6, thumb: 1.35, spread: -0.05 },
  point:     { index: -0.05, middle: 1.6, ring: 1.6, pinky: 1.6, thumb: 0.9, spread: 0 },
  grip:      { index: 1.15, middle: 1.2, ring: 1.25, pinky: 1.3, thumb: 0.7, spread: -0.04 },    // a handle / the broom
  book:      { index: 0.55, middle: 0.6, ring: 0.65, pinky: 0.7, thumb: 0.35, spread: 0.08 },    // under the book, fingers up its side
  thumbs_up: { index: 1.6, middle: 1.6, ring: 1.6, pinky: 1.6, thumb: -0.45, spread: -0.05 },
  claw:      { index: 0.8, middle: 0.85, ring: 0.9, pinky: 0.95, thumb: 0.5, spread: 0.2 },      // shocked hands
};
const CURL2 = 1.15;         // the second joint bends a little more than the knuckle (reads as a natural curl)
const KEYS = ["index", "middle", "ring", "pinky", "thumb", "spread", "k2"];

const shape = (h) => (typeof h === "string" ? HAND_SHAPES[h] : h) || null;

// ── THE ARM ACTS ─────────────────────────────────────────────────────
// Each returns absolute rotations for the bones it owns (the rest stay williePose's),
// plus the hand shapes it wants. `c` is a clock that restarts when the act begins.
// Signs: R arm is at −x. rotation.x −: forward/up. rotation.z −: R arm OUT to his side (L: +).
const lerp = (a, b, k) => a + (b - a) * k;
export const WILLIE_ACTS = {
  wave: (c) => {
    const w = Math.sin(c * 9) * 0.35;
    return { bones: { upperarmR: [-1.25, 0, 0.3], forearmR: [-1.8, 0, w], handR: [-0.2, 1.3, 0] }, hands: { R: "open" }, look: 1 };
  },
  present: (c) => {   // the tour guide: "and over HERE…" — an open palm sweeping out to his side
    const k = 0.5 - 0.5 * Math.cos(Math.min(1, c / 1.2) * Math.PI);
    return { bones: { upperarmR: [-0.6, 0, lerp(0.15, -0.55, k)], forearmR: [lerp(-1.1, -0.5, k), 0, 0], handR: [0, lerp(1.4, 2.8, k), -0.3] }, hands: { R: "open" }, spine: [0, -0.12 * k, 0], look: 1 };
  },
  beckon: (c) => {   // "this way" — palm up, the fingers curling him along
    const f = 0.5 + 0.5 * Math.sin(c * 7);
    return { bones: { upperarmR: [-0.5, 0, 0.2], forearmR: [-1.3, 0, 0], handR: [0, 1.5, 0] }, hands: { R: { index: 0.2 + 1.2 * f, middle: 0.2 + 1.25 * f, ring: 0.25 + 1.3 * f, pinky: 0.3 + 1.3 * f, thumb: -0.1, spread: 0.1 } }, look: 1 };
  },
  shrug: (c) => {
    const k = Math.sin(Math.min(1, c / 0.35) * Math.PI * 0.5);
    return { bones: { upperarmR: [-0.15 * k, 0, -0.3 * k], forearmR: [-1.5 * k, 1.3 * k, 0], handR: [0, 0, 0.2 * k] }, hands: { R: "open" }, head: [0, 0, 0.12 * k], look: 1 };
  },
  thumbs_up: () => ({ bones: { upperarmR: [-0.7, 0, 0.55], forearmR: [-1.5, -0.3, 0], handR: [0, 0.6, 0] }, hands: { R: "thumbs_up" }, look: 1 }),
  facepalm: () => ({ bones: { upperarmR: [-1.3, 0, 1.05], forearmR: [-1.7, 1.2, 0], handR: [0, 1.5, 0.2] }, hands: { R: "open" }, head: [0.35, 0.1, 0], look: 1 }),
  wipe_tear: (c) => {   // with ctl.expr = "sad": a knuckle to the eye, rubbing
    const r = Math.sin(c * 5) * 0.06;
    return { bones: { upperarmR: [-1.05, 0, 1.05], forearmR: [-2.1 + r, 0, 0], handR: [0.35, 1.5, 0] }, hands: { R: "relaxed" }, head: [0.28, 0, 0], look: 1 };
  },
  sweep: (c) => {     // with ctl.hold = "broom": short brisk strokes, his body turning into each one
    const s = Math.sin(c * 3.4);
    return { bones: { upperarmR: [-0.3 + 0.08 * Math.abs(s), 0, 0.12 + 0.28 * s], forearmR: [-0.55, 0, 0.1 * s], handR: [0, 0.3, 0] }, hands: { R: "grip" }, spine: [0.1, -0.12 * s, 0], look: 0, sweep: s };
  },
};

// ── THE BROOM (a prop in his right fist, built from primitives) ─────
const TOON = (hex) => new THREE.MeshToonMaterial({ color: hex, emissive: new THREE.Color(hex).multiplyScalar(0.35) });
export function makeBroom(inkMat) {
  const g = new THREE.Group(); g.name = "wil_broom"; g.visible = false;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.018, 1.3, 7), TOON(0x6b5236));
  shaft.position.y = 0.65;                         // the group's origin is the FLOOR end; +y runs up the handle
  const head = new THREE.Mesh(new THREE.ConeGeometry(0.19, 0.42, 9, 1, true), TOON(0xa88c55));
  head.scale.set(1, 1, 0.55); head.position.y = 0.19; head.material.side = THREE.DoubleSide;
  const band = new THREE.Mesh(new THREE.TorusGeometry(0.05, 0.011, 5, 10), TOON(0x7d6a4c));
  band.rotation.x = Math.PI / 2; band.position.y = 0.33;
  for (const m of [shaft, head, band]) {
    g.add(m);
    if (inkMat) { const h = new THREE.Mesh(m.geometry, inkMat); h.position.copy(m.position); h.rotation.copy(m.rotation); h.scale.copy(m.scale); g.add(h); }
  }
  return g;
}
const GRIP = new THREE.Vector3(), FLOOR = new THREE.Vector3(), UP = new THREE.Vector3(0, 1, 0), DIR = new THREE.Vector3(), Q = new THREE.Quaternion();
const GRIP_ON_SHAFT = 0.95;   // the fist holds the handle this far up from the bristles

// ── THE STATE ────────────────────────────────────────────────────────
export function actingState() {
  const h = () => Object.fromEntries(KEYS.map((k) => [k, 0]));
  return { hands: { L: h(), R: h() }, act: null, actW: 0, actT: 0, prev: null, book: 1, broom: 0, lastAct: null };
}

const ease = (cur, want, dt, rate) => cur + (want - cur) * Math.min(1, dt * rate);

// Run AFTER williePose (it owns the idle/walk/read/point) and BEFORE the face layer.
// Returns { lookUp } so the caller can lift his head off the book.
export function actWillie(B, S, ctl, t, dt, ph, amt, pt, root, broom) {
  const c = ctl || {};
  const set = (n, r, k) => { const b = B[n]; if (!b || !r) return; b.rotation.x = lerp(b.rotation.x, r[0], k); b.rotation.y = lerp(b.rotation.y, r[1], k); b.rotation.z = lerp(b.rotation.z, r[2], k); };
  // the book: away (0) or in hand (1)
  S.book = ease(S.book, c.book === false ? 0 : 1, dt, 5);
  const noBook = 1 - S.book;
  if (noBook > 0.001) {   // the left arm comes down and swings like his right, mirrored
    const frL = Math.sin(ph) * 0.35 * amt;
    set("upperarmL", [frL, 0, 0.08], noBook);
    set("forearmL", [-0.15 + Math.min(0, frL) * 0.4, 0, 0], noBook);
    set("handL", [0, 0, 0], noBook);
  }
  // the act: cross-fade the old one out while the new one comes in
  const want = c.act && WILLIE_ACTS[c.act] ? c.act : null;
  // a new act waits for the old one to ease out, then starts its own clock
  if (want !== S.act && S.actW < 0.03) { S.act = want; S.actT = 0; }
  S.actT += dt;
  S.actW = ease(S.actW, want && want === S.act ? 1 : 0, dt, 6);
  let A = null;
  if (S.act && S.actW > 0.001) {
    A = WILLIE_ACTS[S.act](S.actT);
    const k = S.actW * (1 - pt);                  // a point (williePose) wins over an act
    for (const n in A.bones) set(n, A.bones[n], k);
    if (A.spine) { const b = B.spine; if (b) { b.rotation.x += A.spine[0] * k; b.rotation.y += A.spine[1] * k; b.rotation.z += A.spine[2] * k; } }
    if (A.head) { const b = B.head; if (b) { b.rotation.x += A.head[0] * k; b.rotation.y += A.head[1] * k; b.rotation.z += (A.head[2] || 0) * k; } }
  }
  // the hands: explicit ctl.hands > the act's > the defaults (R: v1's fist, or its point; L: the book)
  for (const side of ["L", "R"]) {
    let tgt = shape(c.hands && c.hands[side]);
    if (!tgt && A && A.hands && A.hands[side] && S.actW > 0.5) tgt = shape(A.hands[side]);
    if (!tgt) {
      if (side === "R") tgt = c.hold === "broom" ? HAND_SHAPES.grip : pt > 0.5 ? HAND_SHAPES.point : HAND_SHAPES.fist;
      else tgt = S.book > 0.5 ? HAND_SHAPES.book : HAND_SHAPES.relaxed;
    }
    const cur = S.hands[side];
    for (const k of KEYS) cur[k] = ease(cur[k], tgt[k] || 0, dt, 12);
    applyHand(B, side, cur);
  }
  // the broom: in his right fist, bristles on the floor
  S.broom = ease(S.broom, c.hold === "broom" ? 1 : 0, dt, 8);
  if (broom) {
    broom.visible = S.broom > 0.5;
    if (broom.visible && B.handR) {
      B.handR.updateWorldMatrix(true, false);
      GRIP.set(0, -0.075, 0.015).applyMatrix4(B.handR.matrixWorld); root.worldToLocal(GRIP);
      const sw = (A && A.sweep) || 0;
      FLOOR.set(GRIP.x + 0.08 + sw * 0.28, 0.02, GRIP.z + 0.32 + Math.abs(sw) * 0.05);
      DIR.subVectors(GRIP, FLOOR).normalize();
      Q.setFromUnitVectors(UP, DIR);
      broom.quaternion.copy(Q);
      broom.position.copy(GRIP).addScaledVector(DIR, -GRIP_ON_SHAFT);
    }
  }
  return { lookUp: Math.max(noBook, A && S.actW > 0.3 ? A.look || 0 : 0) };
}

export function applyHand(B, side, h) {
  const s = side === "R" ? -1 : 1;
  FINGERS.forEach((f, i) => {
    const a = B[f + "1" + side], b = B[f + "2" + side];
    if (!a) return;
    const fan = (i - 1.5) * h.spread * 0.5;     // index forward, pinky back
    a.rotation.set(fan, 0, -s * h[f]);
    if (b) b.rotation.set(0, 0, -s * h[f] * (h.k2 || CURL2));
  });
  const t1 = B["thumb1" + side], t2 = B["thumb2" + side];
  // the thumb points forward-down; folding it swings the tip IN across the palm (y) and DOWN over the fingers (x)
  if (t1) t1.rotation.set(Math.max(0, h.thumb) * 0.55, -s * h.thumb * 0.75, -s * h.thumb * 0.3);
  if (t2) t2.rotation.set(Math.max(0, h.thumb) * 0.45, -s * h.thumb * 0.45, -s * h.thumb * 0.2);
}
